'use client';

import React, { useCallback, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import { formatCriteriaTemplateWithMetadata, getComponentTypeClasses, type TooltipContent } from 'src/utils/criteriaDisplayUtils';
import { Tooltip } from 'src/components/PulseUI';
import { CodeWithTooltip } from 'src/components/Strategy/shared';
import {
  useGetCodeByNameQuery,
  useGetObjectDefinitionsListQuery,
  useGetFieldDefinitionsQuery,
} from 'src/api/redux';
import { formatCode } from 'src/components/Strategy/shared/utils';
import { formatSnakeCaseStatus } from 'src/components/DynamicCriteriaGenerator/components/CRMStatus/config';
import type { CodeData } from 'src/components/Strategy/shared/types';

interface HighlightedCriteriaProps {
  criteriaTemplate: string;
  criteriaInputs: Record<string, unknown>;
  className?: string;
  /** Render `CPT-`/`ICD-`/`DRUG-` codes in plain text as code tooltips. Off for free text it would truncate. */
  linkifyCodes?: boolean;
}

/**
 * Renders tooltip content based on whether it's a string or structured object
 */
const renderTooltipContent = (
  tooltip: string | TooltipContent
): React.ReactNode => {
  if (typeof tooltip === 'string') {
    return (
      <div className="px-3 py-2 bg-level-1 rounded-xl max-w-sm border border-neutral-200 shadow-sm">
        <div className="flex flex-col gap-2 text-primary text-xs font-normal font-['Inter'] leading-tight break-words">
          <div className="whitespace-pre-line">{tooltip}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 py-2 bg-level-1 rounded-xl max-w-sm border border-neutral-200 shadow-sm">
      <div className="flex flex-col gap-2 text-primary text-xs font-normal font-['Inter'] leading-tight break-words">
        {tooltip.title && <div className="font-semibold">{tooltip.title}</div>}
        {tooltip.sections?.map((section: string, i: number) => (
          <div key={`section-${i}`}>{section}</div>
        ))}
      </div>
    </div>
  );
};

const HighlightedCriteria = ({
  criteriaTemplate,
  criteriaInputs,
  className,
  linkifyCodes = true,
}: HighlightedCriteriaProps) => {
  // Fetch MedRM field definitions to map status values to user-configured labels
  const { data: objectDefinitions } = useGetObjectDefinitionsListQuery();
  const { data: fieldDefinitions } = useGetFieldDefinitionsQuery(
    { objectId: objectDefinitions?.results?.[0]?.id },
    { skip: !objectDefinitions?.results?.[0]?.id }
  );

  const statusLabelMap = useMemo(() => {
    const statusField = fieldDefinitions?.results?.find(
      (field) => field?.label?.toLowerCase() === 'status'
    );
    if (!statusField?.options) return new Map<string, string>();
    return new Map(
      statusField.options.map((opt) => [opt.value, opt.label])
    );
  }, [fieldDefinitions]);

  // Enrich CRM status ui_metadata with user-configured labels
  const enrichedInputs = useMemo(() => {
    if (statusLabelMap.size === 0) return criteriaInputs;

    const enriched: Record<string, unknown> = {};
    for (const [key, input] of Object.entries(criteriaInputs)) {
      const typedInput = input as any;
      if (typedInput?.type === 'crm_status' && Array.isArray(typedInput.ui_metadata)) {
        enriched[key] = {
          ...typedInput,
          ui_metadata: typedInput.ui_metadata.map((status: any) => ({
            ...status,
            displayLabel: status.crm_status
              ? statusLabelMap.get(status.crm_status) || status.displayLabel || formatSnakeCaseStatus(status.crm_status)
              : status.displayLabel,
          })),
        };
      } else {
        enriched[key] = input;
      }
    }
    return enriched;
  }, [criteriaInputs, statusLabelMap]);

  // Parse the template into segments with metadata
  const segments = formatCriteriaTemplateWithMetadata(
    criteriaTemplate,
    enrichedInputs
  );

  // Extract hardcoded codes from plain text segments (e.g., "CPT-99213", "ICD-M1234")
  const hardcodedCodes = useMemo(() => {
    if (!linkifyCodes) return [];
    const codeRegex = /(CPT|ICD|DRUG)-([A-Z0-9]+)/gi;
    const codes: { code: string; type: string }[] = [];

    segments.forEach((segment) => {
      if (!segment.isComponent) {
        let match;
        while ((match = codeRegex.exec(segment.text)) !== null) {
          codes.push({
            code: match[2], // Just the code part without prefix
            type: match[1].toUpperCase(), // CPT, ICD, or DRUG
          });
        }
      }
    });

    return codes;
  }, [segments, linkifyCodes]);

  // Fetch metadata for all hardcoded codes
  const codeIds = useMemo(
    () => hardcodedCodes.map((c) => formatCode(c.code, c.type)),
    [hardcodedCodes]
  );

  const { data: codeData } = useGetCodeByNameQuery(
    { codeIds },
    { skip: codeIds.length === 0 }
  );

  // Create a map of code to data for quick lookup
  const codeDataMap = useMemo(() => {
    if (!codeData?.results) return new Map<string, CodeData>();

    const map = new Map<string, CodeData>();
    (codeData.results as CodeData[]).forEach((cd: CodeData) => {
      if (cd.id || cd.code_name) {
        map.set(cd.id || cd.code_name || '', cd);
      }
    });
    return map;
  }, [codeData]);

  // Memoize the keyDown handler to prevent unnecessary re-renders
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
    }
  }, []);

  if (!criteriaTemplate) {
    return null;
  }

  // Helper to render a segment, replacing hardcoded codes with CodeWithTooltip
  const renderSegment = (segment: any, index: number) => {
    if (segment.isComponent) {
      // Existing component logic (code_set, filter_set, etc.) - keep unchanged
      if (segment.tooltip) {
        return (
          <Tooltip
            content={renderTooltipContent(segment.tooltip)}
            position="top"
            slotProps={{
              containerClassName:
                '!bg-transparent !text-primary !shadow-none !border-none',
              contentClassName: '!bg-transparent',
              arrowClassName:
                '!bg-level-1 !border-neutral-200 !border-r !border-b',
            }}
          >
            <span
              className={`font-medium cursor-pointer ${getComponentTypeClasses(
                segment.componentType,
                segment.uiKey
              )}`}
              data-testid={`highlighted-component-${segment.componentType}-${index}`}
              role="button"
              tabIndex={0}
              aria-label={`${segment.componentType} ${segment.text}. Hover for description.`}
              onKeyDown={handleKeyDown}
            >
              {segment.text}
            </span>
          </Tooltip>
        );
      }

      return (
        <span
          className={`font-medium ${getComponentTypeClasses(
            segment.componentType,
            segment.uiKey
          )}`}
        >
          {segment.text}
        </span>
      );
    }

    if (!linkifyCodes) {
      return <span>{segment.text}</span>;
    }

    // Plain text segment - check for hardcoded codes
    const codeRegex = /(CPT|ICD|DRUG)-([A-Z0-9]+)/gi;
    const parts: React.ReactNode[] = [];
    const codesFound: Array<{ type: string; value: string; index: number }> =
      [];
    let match;

    // First pass: find all codes
    while ((match = codeRegex.exec(segment.text)) !== null) {
      codesFound.push({
        type: match[1].toUpperCase(),
        value: match[2],
        index: match.index,
      });
    }

    // If no codes found, return text as-is
    if (codesFound.length === 0) {
      return <span>{segment.text}</span>;
    }

    // Extract the prefix text (everything before first code)
    const prefixText = segment.text.substring(0, codesFound[0].index).trim();

    // Extract the suffix text (everything after last code, typically just ".")
    const lastCode = codesFound[codesFound.length - 1];
    const lastCodeEnd =
      lastCode.index + `${lastCode.type}-${lastCode.value}`.length;
    const suffixMatch = segment.text.substring(lastCodeEnd).match(/[.!?]/);
    const suffixText = suffixMatch ? suffixMatch[0] : '';

    // Rebuild the text with proper grammar
    parts.push(
      <span key={`prefix-${index}`}>
        {prefixText}
        {prefixText ? ' ' : ''}
      </span>
    );

    // Add codes with proper separators
    codesFound.forEach((code, codeIndex) => {
      const formattedCode = formatCode(code.value, code.type);
      const codeInfo = codeDataMap.get(formattedCode);
      const isLastCode = codeIndex === codesFound.length - 1;
      const isSecondToLast = codeIndex === codesFound.length - 2;

      parts.push(
        <CodeWithTooltip
          key={`code-${index}-${codeIndex}`}
          code={code.value}
          type={code.type}
          codeData={codeInfo}
        />
      );

      // Add separator
      if (!isLastCode) {
        parts.push(
          <span key={`sep-${index}-${codeIndex}`}>
            {isSecondToLast ? ', or ' : ', '}
          </span>
        );
      }
    });

    // Add suffix
    if (suffixText) {
      parts.push(<span key={`suffix-${index}`}>{suffixText}</span>);
    }

    return <>{parts}</>;
  };

  return (
    <div
      className={twMerge(
        // family-name: keeps twMerge from reading the arbitrary font as a weight and dropping font-normal
        'text-secondary text-base font-normal font-[family-name:Inter] leading-tight',
        className
      )}
    >
      {segments.map((segment, index) => (
        <React.Fragment key={segment.componentId || `text-${index}`}>
          {renderSegment(segment, index)}
        </React.Fragment>
      ))}
    </div>
  );
};

export default HighlightedCriteria;
